import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { useFilters } from 'hooks/useFilters';
import _debounce from 'lodash/debounce';

import { show } from 'store/slices/alert';
import { certificateSelector, download } from 'store/slices/certificate';

import { getCertificate, getCertificatePathByCountry } from 'utils/compliance';
import { formatViewDate } from 'utils/date';

import Button from 'components/UI/Button/Button';
import FilterToggleGroup from 'components/UI/FilterToggleGroup/FilterToggleGroup';
import Loader from 'components/UI/Loader/Loader';
import NoData from 'components/UI/NoData/NoData';
import SearchInput from 'components/UI/SearchInput/SearchInput';
import Select from 'components/UI/Select/Select';
import Table from 'components/UI/Table/Table';
import Typography from 'components/UI/Typography/Typography';

import { gameJurisdictionListPath, getIcon } from 'components/config/cloud';
import { SEARCH_DELAY } from 'components/config/common';
import { COLUMNS, GAME_TYPE_OPTIONS } from 'components/config/compliance';
import { COUNTRIES } from 'components/config/compliance';
import { data } from 'components/config/compliance';
import { fillDate } from 'components/config/filters';

import { ReactComponent as Download2Icon } from 'images/Icons/Download2.svg';
import { ReactComponent as DownloadIcon } from 'images/Icons/Download.svg';

import styles from './Compliance.module.scss';

const initialState = {
  type: GAME_TYPE_OPTIONS[0].value,
  search: '',
  releaseDates: [],
};

const Compliance = () => {
  const dispatch = useDispatch();

  const { filters, setFilters, handleChange } = useFilters(initialState);

  const inputRef = useRef();

  const loader = useSelector(certificateSelector.getLoading);
  const errors = useSelector(certificateSelector.getError);

  const filtersEnabled = useMemo(
    () => JSON.stringify(filters) !== JSON.stringify(initialState),
    [filters],
  );

  const filteredData = useMemo(
    () =>
      [...data]
        .sort((a, b) => new Date(b.releaseDate) - new Date(a.releaseDate))
        .filter(({ type, gameName, releaseDate }) => {
          if (filters.type && type !== filters.type) return false;

          if (filters.releaseDates.length) {
            const [, month, year] = formatViewDate(new Date(releaseDate)).split(
              '.',
            );

            const matchDate = filters.releaseDates.some((date) => {
              if (date.includes('.')) {
                const [filterMonth, filterYear] = date.split('.');
                return month === filterMonth && year === filterYear;
              }
              return year === date;
            });

            if (!matchDate) return false;
          }

          return !(
            filters.search &&
            !gameName.toLowerCase().includes(filters.search.toLowerCase())
          );
        }),
    [data, filters],
  );

  const rows = useMemo(() => {
    const allGamesRow = COUNTRIES.reduce(
      (acc, country) => ({
        ...acc,
        [country]: (
          <div className={styles.downloadAllCell}>
            <span>All</span>
            <Download2Icon />
          </div>
        ),
      }),
      {
        id: 'allGames',
        gameName: '',
      },
    );

    const rowsByGame = filteredData.map((item) => {
      const { gameName, gameCode } = item;

      const downloadIcons = COUNTRIES.reduce(
        (acc, country) => ({
          ...acc,
          [country]: item[country] ? (
            <DownloadIcon className={styles.downloadIcon} />
          ) : null,
        }),
        {},
      );

      return {
        id: gameCode,
        name: gameName,
        gameName: (
          <div className={styles.gameCell}>
            <div className={styles.gameName}>
              <img
                src={getIcon(gameCode)}
                alt={gameName}
                className={styles.gameIcon}
              />
              <Typography type="text-sm" weight="normal">
                {gameName}
              </Typography>
            </div>
            <Download2Icon className={styles.downloadIcon} />
          </div>
        ),
        ...downloadIcons,
      };
    });

    return [allGamesRow, ...rowsByGame];
  }, [filteredData]);

  const handleSearch = useCallback(
    _debounce(
      ({ target: { value } }) => handleChange('search', value),
      SEARCH_DELAY,
    ),
    [],
  );

  const handleClearFilters = useCallback(() => {
    setFilters(initialState);
    inputRef.current.value = initialState.search;
  }, []);

  const handleDownload = useCallback(
    (row, columnId) => {
      if (row.id === 'allGames') {
        const urls = filteredData.reduce(
          (acc, item) =>
            item[columnId]
              ? [...acc, getCertificatePathByCountry(item.gameCode, columnId)]
              : acc,
          [],
        );

        dispatch(
          download({
            zipFileName: `${columnId} [Certificates]`,
            schema: 'certificates',
            urls,
          }),
        );

        return;
      }

      if (columnId === 'gameName') {
        dispatch(
          download({
            zipFileName: `${row.name} [Certificates]`,
            urls: [getCertificate(row.id)],
          }),
        );

        return;
      }

      dispatch(
        download({
          zipFileName: `${row.name} [Certificate - ${columnId}]`,
          urls: [getCertificatePathByCountry(row.id, columnId)],
        }),
      );
    },
    [filteredData],
  );

  useEffect(() => {
    errors && dispatch(show({ type: 'error', text: errors }));
  }, [errors]);

  return (
    <div className={styles.wrapper}>
      <Typography type="heading-md" weight="medium" uppercase>
        Game certificates
      </Typography>
      <div className={styles.content}>
        <div className={styles.header}>
          <div className={styles.filters}>
            <FilterToggleGroup
              name="type"
              value={filters.type}
              options={GAME_TYPE_OPTIONS}
              onChange={handleChange}
            />
            <div className={styles.searchWrapper}>
              <SearchInput
                ref={inputRef}
                placeholder="Search by game name"
                onChange={handleSearch}
                className={styles.searchInput}
              />
              <Select
                title="Release Date"
                label="releaseDates"
                value={filters.releaseDates}
                items={fillDate(2017)}
                handleSelect={handleChange}
                width={200}
                widthDropdown={265}
                countTags={2}
                multiple
              />
              {filtersEnabled && (
                <Button
                  variant="text"
                  onClick={handleClearFilters}
                  sx={{
                    textDecoration: 'underline',
                  }}
                >
                  Clear filters
                </Button>
              )}
            </div>
          </div>
          <Button
            variant="icon"
            target="_blank"
            rel="noreferrer"
            component={Link}
            startIcon={<DownloadIcon className={styles.downloadIcon} />}
            to={gameJurisdictionListPath}
            download
          >
            All certificates
          </Button>
        </div>
        {filteredData.length ? (
          <Table
            columns={COLUMNS}
            rows={rows}
            onCellClick={(_, row, columnId) => handleDownload(row, columnId)}
            classNames={{ cellRow: styles.cellRow, cell: styles.cell }}
            stickyHeader
          />
        ) : (
          <NoData />
        )}
        {loader && <Loader type="content" />}
      </div>
    </div>
  );
};

export default Compliance;
